import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

// Distance (px) from the bottom within which we consider the user "at the end"
// and keep auto-scrolling as new content streams in.
export const STICKY_THRESHOLD = 96;

export function distanceFromBottom(el) {
  if (!el) return 0;
  return el.scrollHeight - el.scrollTop - el.clientHeight;
}

export function isNearBottom(el, threshold = STICKY_THRESHOLD) {
  return distanceFromBottom(el) <= threshold;
}

// Keeps a scroll container pinned to the bottom while the user is already there,
// but never yanks them down when they've scrolled up to re-read (§9.4). When new
// content arrives and the user is NOT at the bottom, `atBottom` flips to false so
// a "↓ novas mensagens" affordance can be shown.
export default function useStickyScroll(dep, { threshold = STICKY_THRESHOLD } = {}) {
  const ref = useRef(null);
  const stickRef = useRef(true); // whether we should follow the bottom
  const [atBottom, setAtBottom] = useState(true);

  const onScroll = useCallback(() => {
    const near = isNearBottom(ref.current, threshold);
    stickRef.current = near;
    setAtBottom(near);
  }, [threshold]);

  const scrollToBottom = useCallback((behavior = "smooth") => {
    const el = ref.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior });
    stickRef.current = true;
    setAtBottom(true);
  }, []);

  // Runs after each dependency change (new message / streamed token). If the user
  // was glued to the bottom we follow the new content; otherwise we leave them be.
  useLayoutEffect(() => {
    if (stickRef.current && ref.current) {
      ref.current.scrollTop = ref.current.scrollHeight;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dep]);

  // Recompute on mount so the button state matches reality on first paint.
  useEffect(() => {
    onScroll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { ref, atBottom, onScroll, scrollToBottom };
}
